import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useSwipeable } from 'react-swipeable';

import PropTypes from 'prop-types';

import { useApp } from 'apps/auth/hooks/selectors';

import PopupPortal from './PopupPortal';
import { getoutAnimation, Overlay, PopupBox, PopupBoxInner, PopupOuter } from './styles';

type PopupOuterProps = React.ComponentProps<typeof PopupOuter>;
type PopupBoxProps = React.ComponentProps<typeof PopupBox>;
type PopupBoxInnerProps = React.ComponentProps<typeof PopupBoxInner>;

const StyledPopupOuter = PopupOuter as React.ComponentType<PopupOuterProps & { fullwidthPopup?: boolean }>;
const StyledPopupBox = PopupBox as React.ComponentType<PopupBoxProps & { animated?: boolean; bottom?: string; fullwidthPopup?: boolean }>;
const StyledPopupBoxInner = PopupBoxInner as React.ComponentType<PopupBoxInnerProps & { isShowSpinner?: boolean; fullwidthPopup?: boolean }>;

type PopupOverlayClickHandler = (event?: React.MouseEvent) => void;
type PopupData = React.ReactElement<{ onOverlayClick?: PopupOverlayClickHandler }> | null;

export interface PopupNewHandle {
    open: (data?: PopupData) => void;
    close: () => void;
}

export interface PopupNewProps {
    as?: React.ComponentType<{ children: React.ReactNode }>;
    getPopupComponent?: (data: PopupData) => React.ReactNode;
    overflowHidden?: boolean;
    fullwidthPopup?: boolean;
}

type PopupState = {
    isShow: boolean;
    animationEnd: boolean;
};

const PopupNew = React.forwardRef<PopupNewHandle, PopupNewProps>(function PopupNew(props, innerRef) {
    const { isShowSpinner, isShowModal } = useApp<{ isShowSpinner: boolean; isShowModal: boolean }>() ?? {};
    const overlayRef = useRef<HTMLDivElement>(null);
    const interactionStartedInsidePopupRef = useRef(false);
    const [isOpen, setOpen] = useState(false);
    const [state, setState] = useState<PopupState>({
        isShow: false,
        animationEnd: false,
    });
    const [data, setData] = useState<PopupData>(null);
    const [bottom, setBottom] = useState('');
    const { as: Wrapper = PopupPortal } = props;

    useImperativeHandle(
        innerRef,
        () => ({
            open: (body: PopupData = null) => {
                setOpen(true);
                if (body) {
                    setData(body);
                }
            },
            close: () => setOpen(false),
        }),
        [],
    );

    useEffect(() => {
        if (__isBrowser__ && isOpen) {
            if (!props.overflowHidden) {
                document.body.style.overflow = 'hidden';
            }
            document.addEventListener('keydown', keyPressHandler);
        }
        return () => {
            if (__isBrowser__ && isOpen && !isShowModal && !props.overflowHidden) {
                document.body.style.overflow = 'visible';
                document.removeEventListener('keydown', keyPressHandler);
            }
        };
    }, [isShowModal, isOpen, props.overflowHidden]);

    useEffect(() => {
        if (isOpen) {
            window.visualViewport?.addEventListener('resize', checkKeyboardOpened);
            setState((prevState) => ({
                ...prevState,
                isShow: true,
            }));
        } else if (!isOpen && state.isShow) {
            setState((prevState) => ({
                ...prevState,
                animationEnd: true,
            }));
        }
        return () => {
            window.visualViewport?.removeEventListener('resize', checkKeyboardOpened);
            setBottom('');
        };
    }, [isOpen, state.isShow]);

    const checkKeyboardOpened = () => {
        const initialScreenSize = document.body.clientHeight;
        const viewportHeight = window.visualViewport?.height ?? initialScreenSize;
        const offset = window.visualViewport?.offsetTop ?? 0;
        const calculatedBottom = `${initialScreenSize - viewportHeight - offset + 24}px`;
        const isKeyboardOpened = viewportHeight < initialScreenSize;
        setBottom(isKeyboardOpened ? calculatedBottom : '');
    };

    const slide = () => {
        data?.props?.onOverlayClick?.();
        setOpen(false);
    };

    const handlers = useSwipeable({
        onSwipedDown: slide,
        delta: 20,
        preventScrollOnSwipe: true,
        trackTouch: true,
    });

    const keyPressHandler = (event: KeyboardEvent) => {
        if (event.key === 'Escape' || event.keyCode === 27) {
            event.preventDefault();
            setOpen(false);
        }
    };

    const overlayPointerDown = (event: React.MouseEvent | React.TouchEvent) => {
        interactionStartedInsidePopupRef.current = !!overlayRef.current?.contains(event.target as Node);
    };

    const overlayClick = (event: React.MouseEvent) => {
        if (overlayRef.current && !interactionStartedInsidePopupRef.current && !overlayRef.current.contains(event.target as Node)) {
            data?.props?.onOverlayClick?.(event);
            event.preventDefault();
            setOpen(false);
        }
        interactionStartedInsidePopupRef.current = false;
    };

    const closeAnimationPopup = useCallback((event: React.AnimationEvent) => {
        if (event?.animationName === getoutAnimation?.name) {
            flushSync(() => {
                setState({
                    isShow: false,
                    animationEnd: false,
                });
                setData(null);
            });
        }
    }, []);

    const animated = useMemo(() => !isOpen && state.animationEnd, [isOpen, state.animationEnd]);

    if (!state.isShow) {
        return null;
    }

    return (
        <Wrapper>
            <div id="popup-body" {...handlers}>
                <Overlay onMouseDownCapture={overlayPointerDown} onTouchStartCapture={overlayPointerDown} onClick={overlayClick}>
                    <StyledPopupOuter fullwidthPopup={props.fullwidthPopup}>
                        <StyledPopupBox animated={animated} onAnimationEnd={closeAnimationPopup} bottom={bottom} fullwidthPopup={props.fullwidthPopup}>
                            <StyledPopupBoxInner ref={overlayRef} isShowSpinner={isShowSpinner} fullwidthPopup={props.fullwidthPopup}>
                                {props.getPopupComponent?.(data) || data}
                            </StyledPopupBoxInner>
                        </StyledPopupBox>
                    </StyledPopupOuter>
                </Overlay>
            </div>
        </Wrapper>
    );
});

PopupNew.propTypes = {
    getPopupComponent: PropTypes.func,
    as: PropTypes.func,
    overflowHidden: PropTypes.bool,
    fullwidthPopup: PropTypes.bool,
};

export default PopupNew;
